import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CustomerHeader from '../components/CustomerHeader';
import CustomAlert from '../components/CustomAlert';
import { FaCheckCircle, FaClock, FaExclamationCircle, FaStar, FaComment } from 'react-icons/fa';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
`;

const ContentContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.theme.primaryDark || '#c00000'};
  }

  &:active {
    transform: translateX(-2px);
  }
`;

const Title = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ComplaintCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ComplaintHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ComplaintInfo = styled.div`
  flex: 1;
`;

const ComplaintNumber = styled.h3`
  color: ${props => props.theme.textPrimary};
  margin: 0 0 0.25rem;
  font-size: 1.1rem;
`;

const ComplaintDate = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
  margin: 0;
`;

const StatusBadge = styled.span<{ status: 'resolved' | 'pending' | 'in-progress' }>`
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  
  ${props => {
    if (props.status === 'resolved') {
      return `
        background: #e8f5e9;
        color: #2e7d32;
      `;
    } else if (props.status === 'in-progress') {
      return `
        background: #e3f2fd;
        color: #1976d2;
      `;
    }
    return `
      background: #fff3e0;
      color: #f57c00;
    `;
  }}
`;

const ComplaintBody = styled.div`
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme.border};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const InfoLabel = styled.span`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
`;

const InfoValue = styled.span`
  color: ${props => props.theme.textPrimary};
  font-size: 0.85rem;
  font-weight: 600;
`;

const ResolutionSection = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

const ResolutionTitle = styled.h4`
  color: ${props => props.theme.textPrimary};
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ResolutionText = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button<{ variant?: 'confirm' | 'reject' | 'feedback' }>`
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  ${props => {
    if (props.variant === 'confirm') {
      return `
        background: #4caf50;
        color: white;
        
        &:hover {
          background: #45a049;
        }
      `;
    } else if (props.variant === 'reject') {
      return `
        background: #f44336;
        color: white;
        
        &:hover {
          background: #da190b;
        }
      `;
    }
    return `
      background: ${props.theme.primary};
      color: white;
      
      &:hover {
        background: ${props.theme.primaryDark};
      }
    `;
  }}
`;

const FeedbackModal = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isVisible'
})<{ isVisible: boolean }>`
  display: ${props => props.isVisible ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
`;

const FeedbackContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 400px;
  width: 100%;
`;

const FeedbackTitle = styled.h3`
  color: ${props => props.theme.textPrimary};
  margin: 0 0 1rem;
  text-align: center;
`;

const StarRating = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const StarWrapper = styled.div<{ filled: boolean }>`
  font-size: 2rem;
  cursor: pointer;
  color: ${props => props.filled ? '#ffc107' : '#e0e0e0'};
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.1);
  }
`;

const FeedbackTextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 0.9rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  margin-top: 1rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const FeedbackButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  
  ${props => props.variant === 'secondary' ? `
    background: white;
    color: ${props.theme.textPrimary};
    border: 2px solid ${props.theme.border};
    
    &:hover {
      background: ${props.theme.surface};
    }
  ` : `
    background: ${props.theme.primary};
    color: white;
    border: none;
    
    &:hover {
      background: ${props.theme.primaryDark};
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  background: white;
  border-radius: 12px;
  margin-top: 2rem;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  color: ${props => props.theme.border};
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 1rem;
`;

const ConfirmComplaintsResolution: React.FC = () => {
  const navigate = useNavigate();
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState<string | null>(null);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  
  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type?: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title?: string;
    message: string;
    onConfirm?: () => void;
  }>({ isVisible: false, message: '' });

  const complaints = [
    {
      id: 'CMP-001',
      number: '#C498501',
      date: '2024-09-10',
      type: 'Malfunction',
      product: 'Cooler',
      status: 'resolved' as const,
      technician: 'John Smith',
      resolutionDate: '2024-09-12',
      resolution: 'Replaced faulty compressor and recharged refrigerant. Unit is now cooling properly.',
      actionTaken: 'Component Replacement'
    },
    {
      id: 'CMP-002',
      number: '#C498502',
      date: '2024-09-08',
      type: 'Service Quality',
      product: 'Fountain',
      status: 'resolved' as const,
      technician: 'Mike Johnson',
      resolutionDate: '2024-09-11',
      resolution: 'Cleaned and sanitized all dispenser nozzles. Adjusted syrup-to-carbonation ratio.',
      actionTaken: 'Maintenance & Adjustment'
    },
    {
      id: 'CMP-003',
      number: '#C498503',
      date: '2024-09-05',
      type: 'Asset Damage',
      product: 'Vending Machine',
      status: 'in-progress' as const,
      technician: 'Sarah Williams',
      resolutionDate: '',
      resolution: 'Technician visit scheduled for tomorrow to assess damage.',
      actionTaken: 'Pending Assessment'
    }
  ];

  const handleConfirmResolution = (complaintId: string) => {
    setAlert({
      isVisible: true,
      type: 'confirm',
      title: 'Confirm Resolution',
      message: 'Are you satisfied with the resolution provided for this complaint?',
      onConfirm: () => {
        setSelectedComplaint(complaintId);
        setShowFeedback(true);
        setAlert({ isVisible: false, message: '' });
      }
    });
  };

  const handleRejectResolution = (complaintId: string) => {
    setAlert({
      isVisible: true,
      type: 'confirm',
      title: 'Reject Resolution',
      message: 'Are you sure you want to reject this resolution? The complaint will be reopened.',
      onConfirm: () => {
        setAlert({
          isVisible: true,
          type: 'success',
          title: 'Resolution Rejected',
          message: 'The complaint has been reopened and escalated to management.',
          onConfirm: () => {
            navigate('/dashboard');
          }
        });
      }
    });
  };

  const handleSubmitFeedback = () => {
    if (rating === 0) {
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Rating Required',
        message: 'Please provide a rating before submitting.'
      });
      return;
    }

    setShowFeedback(false);
    setAlert({
      isVisible: true,
      type: 'success',
      title: 'Thank You!',
      message: 'Your feedback has been submitted successfully.',
      onConfirm: () => {
        navigate('/dashboard');
      }
    });
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'resolved':
        return React.createElement(FaCheckCircle as any, {});
      case 'in-progress':
        return React.createElement(FaClock as any, {});
      default:
        return React.createElement(FaExclamationCircle as any, {});
    }
  };

  const resolvedComplaints = complaints.filter(c => c.status === 'resolved');

  return (
    <PageContainer>
      <CustomerHeader />
      <ContentContainer>
        <BackButton onClick={() => navigate('/dashboard')}>
          ← Back
        </BackButton>
        <Title>Confirm Complaints Resolution</Title>
        
        {resolvedComplaints.length > 0 ? (
          resolvedComplaints.map(complaint => (
            <ComplaintCard key={complaint.id}>
              <ComplaintHeader>
                <ComplaintInfo>
                  <ComplaintNumber>{complaint.number}</ComplaintNumber>
                  <ComplaintDate>Filed on: {complaint.date}</ComplaintDate>
                </ComplaintInfo>
                <StatusBadge status={complaint.status}>
                  {getStatusIcon(complaint.status)}
                  {complaint.status === 'resolved' ? 'Resolved' : 'In Progress'}
                </StatusBadge>
              </ComplaintHeader>
              
              <ComplaintBody>
                <InfoRow>
                  <InfoLabel>Complaint Type:</InfoLabel>
                  <InfoValue>{complaint.type}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Product:</InfoLabel>
                  <InfoValue>{complaint.product}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Technician:</InfoLabel>
                  <InfoValue>{complaint.technician}</InfoValue>
                </InfoRow>
                {complaint.resolutionDate && (
                  <InfoRow>
                    <InfoLabel>Resolution Date:</InfoLabel>
                    <InfoValue>{complaint.resolutionDate}</InfoValue>
                  </InfoRow>
                )}
                
                <ResolutionSection>
                  <ResolutionTitle>
                    {React.createElement(FaCheckCircle as any, { color: "#4caf50" })}
                    Resolution Details
                  </ResolutionTitle>
                  <ResolutionText>{complaint.resolution}</ResolutionText>
                  <InfoRow style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                    <InfoLabel>Action Taken:</InfoLabel>
                    <InfoValue>{complaint.actionTaken}</InfoValue>
                  </InfoRow>
                </ResolutionSection>

                {complaint.status === 'resolved' && (
                  <ActionButtons>
                    <ActionButton 
                      variant="confirm"
                      onClick={() => handleConfirmResolution(complaint.id)}
                    >
                      {React.createElement(FaCheckCircle as any, {})} Confirm
                    </ActionButton>
                    <ActionButton 
                      variant="reject"
                      onClick={() => handleRejectResolution(complaint.id)}
                    >
                      {React.createElement(FaExclamationCircle as any, {})} Reject
                    </ActionButton>
                    <ActionButton 
                      variant="feedback"
                      onClick={() => {
                        setSelectedComplaint(complaint.id);
                        setShowFeedback(true);
                      }}
                    >
                      {React.createElement(FaComment as any, {})} Feedback
                    </ActionButton>
                  </ActionButtons>
                )}
              </ComplaintBody>
            </ComplaintCard>
          ))
        ) : (
          <EmptyState>
            <EmptyIcon>
              {React.createElement(FaCheckCircle as any, {})}
            </EmptyIcon>
            <EmptyText>No resolved complaints to confirm</EmptyText>
          </EmptyState>
        )}
      </ContentContainer>

      <FeedbackModal isVisible={showFeedback}>
        <FeedbackContent>
          <FeedbackTitle>Rate Our Service</FeedbackTitle>
          
          <StarRating>
            {[1, 2, 3, 4, 5].map((star) => (
              <StarWrapper
                key={star}
                filled={star <= rating}
                onClick={() => setRating(star)}
              >
                {React.createElement(FaStar as any, {})}
              </StarWrapper>
            ))}
          </StarRating>
          
          <FeedbackTextArea
            placeholder="Share your feedback about the resolution..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          
          <FeedbackButtons>
            <Button variant="secondary" onClick={() => setShowFeedback(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmitFeedback}>
              Submit
            </Button>
          </FeedbackButtons>
        </FeedbackContent>
      </FeedbackModal>

      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ isVisible: false, message: '' })}
        onConfirm={alert.onConfirm}
      />
    </PageContainer>
  );
};

export default ConfirmComplaintsResolution;